// Turns the vault's message log (Data/messages.csv) into the activity feed and the conversations the pages show.
// The messages themselves stay in the mail app; the log keeps one line for each.
import { PARTY_KEYS, shortDate, type MessageRecord, type PartyKey } from '@serb/shared';
import { daysBetween } from '../../lib/dates';
import type { Approval } from '../approvals/sample';
import type { ActivityEvent, ActivityStatus, Conversation, Message, Thread } from './sample';

/** How many days an incoming message still counts as "new" */
const NEW_DAYS = 2;

const sentence = (m: MessageRecord) => [m.subject, m.summary].filter(Boolean).join(': ');

/** A message that is the last in its conversation with that party, and went out, is waiting for an answer */
function statusOf(m: MessageRecord, lastOfThread: boolean, today: string): ActivityStatus {
  if (m.direction === 'in' && m.needsReply) return 'Needs Attention';
  if (m.direction === 'out' && lastOfThread) return 'Awaiting Reply';
  if (m.direction === 'in' && daysBetween(m.date, today) <= NEW_DAYS) return 'New Update';
  return 'Updated';
}

const MESSAGE_KINDS: Partial<Record<Approval['kind'], PartyKey>> = {
  'agent-message': 'Overseas Agent',
  'customer-message': 'Customer',
};

/** The feed, newest first: every logged message, plus the message cards waiting for your approval */
export function buildEvents(messages: MessageRecord[], approvals: Approval[], today: string): ActivityEvent[] {
  const lastOf = new Map<string, string>();
  for (const m of messages) if (!lastOf.has(`${m.srb}|${m.party}`)) lastOf.set(`${m.srb}|${m.party}`, m.id); // newest first
  const logged = messages.map<ActivityEvent>((m) => ({
    id: m.id,
    date: shortDate(m.date),
    time: '',
    srb: m.srb,
    party: m.party,
    org: m.org,
    subject: m.subject || m.summary,
    text: m.summary,
    status: statusOf(m, lastOf.get(`${m.srb}|${m.party}`) === m.id, today),
  }));
  const pending = approvals
    .filter((a) => a.status === 'pending' && MESSAGE_KINDS[a.kind])
    .map<ActivityEvent>((a) => ({
      id: `card-${a.id}`,
      date: shortDate(today),
      time: '',
      srb: a.srb,
      party: MESSAGE_KINDS[a.kind]!,
      org: a.to ?? '',
      subject: a.title,
      text: a.summary,
      status: 'Pending Approval',
    }));
  return [...pending, ...logged];
}

/** One conversation per shipment that has messages, newest first */
export function buildConversations(messages: MessageRecord[], events: ActivityEvent[]): Conversation[] {
  const bySrb = new Map<string, MessageRecord[]>();
  for (const m of messages) if (m.srb) bySrb.set(m.srb, [...(bySrb.get(m.srb) ?? []), m]);
  return [...bySrb].map(([srb, all]) => {
    const latest = all[0]!; // newest first
    const mine = events.filter((e) => e.srb === srb);
    const thread = (party: PartyKey): Thread => {
      const list = all.filter((m) => m.party === party);
      if (!list.length) return { contact: '', status: 'No messages yet', messages: [] };
      return {
        contact: `Main contact: ${list[0]!.org}`,
        status: list[0]!.subject || list[0]!.summary,
        messages: [...list].reverse().map<Message>((m) => ({
          id: m.id,
          date: shortDate(m.date),
          time: '',
          from: m.direction === 'out' ? 'You' : m.org,
          text: sentence(m),
        })),
      };
    };
    return {
      srb,
      summary: latest.summary || latest.subject,
      last: shortDate(latest.date),
      unread: mine.filter((e) => e.status === 'Needs Attention' || e.status === 'New Update').length,
      followUp: mine.some((e) => e.status === 'Needs Attention' || e.status === 'Awaiting Reply'),
      ai: latest.summary || latest.subject,
      threads: Object.fromEntries(PARTY_KEYS.map((p) => [p, thread(p)])) as Record<PartyKey, Thread>,
      next: [],
    };
  });
}
